/**
 * videoCalls.service.ts
 *
 * Typed API wrapper for the account-wide Video Calls list (Video Calls section).
 * Backed by GET /api/v5/video-calls (VoiceVideoController::allVideoCalls), which
 * returns the same per-row shape as the per-patient comm-log video calls plus a
 * joined patientName. Uses the shared apiClient (JWT + X-Target-Company injected).
 */
import { apiClient } from './apiClient';
import type { ApiResponse, PaginatedResponse } from '@/types/api.types';

/** One row from GET /video-calls — mirrors VoiceVideoController::mapVideoCallRow. */
export interface VideoCallListItem {
  videoCallID: string;
  patientID: string;
  patientName: string;
  roomName: string;
  duration: string;
  durationFormatted: string;
  compositionStatus: string;
  /** Signed, time-limited playback URL; null unless compositionStatus === 'available'. */
  recordingUrl: string | null;
  /** Human-readable status when no playable recording (processing / failed). */
  recordingMessage: string | null;
  transcript: string;
  createDate: string;
  endDate: string;
}

export interface VideoCallListParams {
  page?: number;
  perPage?: number;
  search?: string;
  dateFrom?: string;
  dateTo?: string;
}

/**
 * Fetch a paginated list of all video calls for the account.
 */
export function listVideoCalls(
  params: VideoCallListParams = {},
  options: { signal?: AbortSignal } = {},
): Promise<PaginatedResponse<VideoCallListItem>> {
  return apiClient
    .get<PaginatedResponse<VideoCallListItem>>('/video-calls', {
      params,
      signal: options.signal,
    })
    .then((r) => r.data);
}

// ─── Scorecards (AI call scoring) ────────────────────────────────────────────

/** One timecoded, speaker-labeled transcript segment (new calls only). */
export interface TranscriptSegment {
  start: number;
  end: number;
  speaker: string;
  text: string;
}

export interface ScorecardCitation {
  timecode: string;
  quote: string;
}

/** One field's evaluation within a generated scorecard. */
export interface ScorecardFieldResult {
  fieldId: number;
  name: string;
  weight: number;
  score: number | null;
  bullets: string[];
  citations: ScorecardCitation[];
}

/** A generated (cached) evaluation for a video call. */
export interface ScorecardResult {
  videoCallScorecardID: number;
  scorecardTemplateID: number;
  overallScore: number;
  summaryTakeaways: string[];
  result: ScorecardFieldResult[];
  modelId: string;
  createDate: string;
  updatedDate: string;
}

/** The active template summary returned alongside a call's scorecard. */
export interface ScorecardTemplateSummary {
  scorecardTemplateID: number;
  name: string;
  ratingMin: number;
  ratingMid: number | null;
  ratingMax: number;
  fields: { scorecardFieldID: number; name: string; weight: number }[];
}

export interface VideoCallScorecardResponse {
  scorecard: ScorecardResult | null;
  template: ScorecardTemplateSummary | null;
  transcript: { text: string; segments: TranscriptSegment[] };
}

/** Fetch the cached scorecard (if any), active template, and transcript. */
export function getVideoCallScorecard(
  videoCallId: string,
  options: { signal?: AbortSignal } = {},
): Promise<VideoCallScorecardResponse> {
  return apiClient
    .get<ApiResponse<VideoCallScorecardResponse>>(`/video-calls/${videoCallId}/scorecard`, {
      signal: options.signal,
    })
    .then((r) => r.data.data);
}

/**
 * Kick off (re)scoring for a video call. The actual Bedrock evaluation runs
 * async on the worker (GenerateVideoCallScorecardJob) — this only validates
 * and dispatches, resolving almost immediately with `{status: 'pending'}`.
 * Poll `getVideoCallScorecard` to see the result land.
 */
export function generateVideoCallScorecard(
  videoCallId: string,
  opts: { regenerate?: boolean } = {},
): Promise<{ status: 'pending' }> {
  return apiClient
    .post<ApiResponse<{ status: 'pending' }>>(
      `/video-calls/${videoCallId}/scorecard`,
      { regenerate: opts.regenerate ?? false },
    )
    .then((r) => r.data.data);
}

// ─── Analytics (account-wide scorecard aggregation) ──────────────────────────

/** A per-field average score, keyed to the active template's field list. */
export interface VideoCallAnalyticsFieldScore {
  fieldId: number;
  name: string;
  score: number | null;
}

/** One day's aggregate — overall average plus per-field averages, for either the team or one user. */
export interface VideoCallAnalyticsTrendPoint {
  date: string;
  avgOverallScore: number;
  fields: VideoCallAnalyticsFieldScore[];
}

/** One team member's daily trend, for drawing a per-person chart line (People filter). */
export interface VideoCallAnalyticsUserTrend {
  userID: number;
  name: string;
  points: VideoCallAnalyticsTrendPoint[];
}

export interface VideoCallAnalyticsTrend {
  /** Whole-account daily average — the constant "Team Average" line. */
  teamAverage: VideoCallAnalyticsTrendPoint[];
  /** Per-user daily series, one entry per team member with any scored calls in range. */
  byUser: VideoCallAnalyticsUserTrend[];
}

export interface VideoCallAnalyticsUserRow {
  userID: number;
  name: string;
  title: string;
  overallScore: number | null;
  fields: VideoCallAnalyticsFieldScore[];
}

export interface VideoCallAnalyticsTotal {
  overallScore: number | null;
  fields: VideoCallAnalyticsFieldScore[];
}

/**
 * How much of the account this payload covers. `'self'` means the server
 * narrowed every figure to the signed-in user's own calls (staff below the
 * manager level), so the team-wide chrome must not be drawn over it — the
 * "Team Average" line and "Total" row would just be their own numbers under a
 * borrowed label.
 */
export type VideoCallAnalyticsScope = 'account' | 'self';

export interface VideoCallAnalyticsResponse {
  template: ScorecardTemplateSummary | null;
  scope: VideoCallAnalyticsScope;
  trend: VideoCallAnalyticsTrend;
  total: VideoCallAnalyticsTotal | null;
  byUser: VideoCallAnalyticsUserRow[];
}

export interface VideoCallAnalyticsParams {
  dateFrom?: string;
  dateTo?: string;
}

/** Account-wide scorecard trend + per-team-member breakdown (Video Calls > Analytics). */
export function getVideoCallAnalytics(
  params: VideoCallAnalyticsParams = {},
  options: { signal?: AbortSignal } = {},
): Promise<VideoCallAnalyticsResponse> {
  return apiClient
    .get<ApiResponse<VideoCallAnalyticsResponse>>('/video-calls/analytics', {
      params,
      signal: options.signal,
    })
    .then((r) => r.data.data);
}
